/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import {
  ChartColumn,
  LayoutDashboard,
  Key,
  FileText,
  Wallet,
  Box,
  Users,
  UsersRound,
  Ticket,
  User,
  Command,
  Radio,
  Sparkles,
  PlayCircle,
  CreditCard,
  ListTodo,
  Settings,
  ShoppingBag,
  ClipboardList,
  Inbox,
  Store,
  Workflow,
  Tag,
  Gift,
  FileCode,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { marketingApex, withDocsLang } from '@/lib/host'
import { WORKSPACE_IDS } from '@/components/layout/lib/workspace-registry'
import { type SidebarData } from '@/components/layout/types'

// 已建好 docs.<主域> 文档站的部署;不在列表的部署菜单里文档项置灰。
const DOCS_READY_APEXES = new Set(['omniapi.co', 'aiproxys.net', '4allapi.com'])

export function useSidebarData(): SidebarData {
  const { t, i18n } = useTranslation()

  return {
    workspaces: [
      {
        id: WORKSPACE_IDS.DEFAULT,
        name: '', // Dynamically fetches system name
        logo: Command,
        plan: '', // Dynamically fetches system version
      },
    ],
    navGroups: [
      // ── Group 1 (template .sb-nav top block):
      //    仪表盘 / 在线体验 / 模型广场 / 定价 ──
      {
        id: 'general',
        title: t('General'),
        items: [
          {
            title: t('Dashboard'),
            // Dashboard overview (/dashboard index redirects to the default
            // overview section). Previously pointed at the /dashboard/models
            // sub-page by mistake.
            url: '/dashboard',
            activeUrls: ['/dashboard'],
            icon: LayoutDashboard,
          },
          {
            title: t('Online Experience'),
            url: '/playground',
            icon: PlayCircle,
          },
          {
            title: t('Flow'),
            url: '/flow',
            icon: Workflow,
          },
          {
            // 后台模型广场 → /models (console working page). The whole console
            // is pinned to the app subdomain (see _authenticated redirect), so
            // this relative link always resolves to api.4allapi.com/models.
            title: t('Model Square'),
            url: '/models',
            activeUrls: ['/models', '/model-pricing'],
            icon: Store,
          },
          {
            // 后台定价 / 价格工作台 → /pricing (console price workbench).
            title: t('Pricing'),
            url: '/pricing',
            activeUrls: ['/pricing'],
            icon: Tag,
          },
        ],
      },
      // ── Group 2 (template middle block):
      //    API 令牌 / 使用日志 / 任务日志 ──
      {
        id: 'console',
        title: t('Console'),
        items: [
          {
            title: t('API Keys'),
            url: '/keys',
            icon: Key,
          },
          {
            title: t('Usage Logs'),
            url: '/usage-logs/common',
            icon: FileText,
          },
          {
            title: t('Task Logs'),
            url: '/usage-logs/task',
            activeUrls: ['/usage-logs/drawing'],
            configUrls: ['/usage-logs/drawing', '/usage-logs/task'],
            icon: ListTodo,
          },
          {
            // 子账号 — present in template but new-api has no sub-account
            // backend yet. Rendered as a non-navigating "Coming soon"
            // placeholder (never links to a real route).
            title: t('Sub-accounts'),
            url: '/',
            icon: UsersRound,
            disabled: true,
            badge: t('Coming soon'),
          },
        ],
      },
      // ── Group 3 (template bottom block):
      //    充值中心 / 邀请奖励(新) / API 文档 + 账户设置
      //    (账户设置 promoted from removed profile popup) ──
      {
        id: 'personal',
        title: t('Personal'),
        items: [
          {
            title: t('Top-up Center'),
            url: '/wallet',
            icon: Wallet,
          },
          {
            title: t('Invite Rewards'),
            url: '/invite',
            icon: Gift,
            badge: t('New'),
          },
          {
            title: t('API Docs'),
            // External documentation site — opens in a new tab (rendered as a
            // native <a target="_blank"> with an external-link indicator icon).
            // Deployments with a live docs site are clickable; anything else
            // renders a muted non-clickable placeholder until its docs exist.
            url: withDocsLang(`https://docs.${marketingApex()}`, i18n.language),
            external: true,
            icon: FileCode,
            ...(!DOCS_READY_APEXES.has(marketingApex())
              ? { disabled: true, badge: t('Coming soon') }
              : {}),
          },
          {
            title: t('Account Settings'),
            url: '/profile',
            icon: User,
          },
        ],
      },
      {
        id: 'admin',
        title: t('Admin'),
        items: [
          {
            // 数据分析(源自 default 主题的数据看板):落地模型调用分析,
            // 页内可切用户分析;admin 组整组仅管理员可见。
            title: t('Analytics'),
            url: '/dashboard/models',
            activeUrls: ['/dashboard/models', '/dashboard/users'],
            icon: ChartColumn,
          },
          { title: t('Task Plugins'), url: '/task-plugins', icon: Box },
          {
            title: t('Channels'),
            url: '/channels',
            icon: Radio,
          },
          {
            title: t('Models'),
            url: '/model-admin/metadata',
            icon: Box,
          },
          {
            // Playground consumes the audited DSH catalog for task-context
            // selection. This admin entry stays disabled because no expert
            // management CRUD exists; never link it to the removed /skills UI.
            title: t('Agent Skills'),
            // The empty URL also keeps this placeholder out of the command
            // menu, which only registers navigation items with a truthy URL.
            url: '',
            icon: Sparkles,
            disabled: true,
            badge: t('Coming soon'),
          },
          {
            title: t('Users'),
            url: '/users',
            icon: Users,
          },
          {
            title: t('Flow Management'),
            url: '/flow-admin',
            icon: Workflow,
          },
          {
            title: t('Redemption Codes'),
            url: '/redemption-codes',
            icon: Ticket,
          },
          {
            title: t('Subscription Management'),
            url: '/subscriptions',
            icon: CreditCard,
          },
          {
            title: t('System Settings'),
            url: '/system-settings/site',
            activeUrls: ['/system-settings'],
            icon: Settings,
          },
          {
            title: t('BOOTH Card Vendor'),
            icon: Store,
            activeUrls: ['/booth'],
            flat: true,
            items: [
              {
                title: t('BOOTH SKUs'),
                url: '/booth/sku',
                icon: ShoppingBag,
              },
              {
                title: t('BOOTH Orders'),
                url: '/booth/order',
                icon: ClipboardList,
              },
              {
                title: t('BOOTH Requests'),
                url: '/booth/requests',
                icon: Inbox,
              },
            ],
          },
        ],
      },
    ],
  }
}
